import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExerciseAction, IExercise } from '../../../core/models/exercise';
import { saveExerciseAnswer } from '../queries/exercises';
import { useExerciseStore } from '../stores/exercisesStore';
import { useGetNextExercise } from './useGetNextExercise';
import { useExercisePageStore } from '../stores/exercisesPageStore';

export const useDisciplineExerciseHook = () => {
  const { discipline, module, subject, slug } = useParams();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [isContentModal, setIsContentModal] = useState(false);
  const [rightAnswer, setRightAnswer] = useState<boolean>(false);
  
  const { exercisesList, setExercisesList } = useExerciseStore();
  const { setPreviousExercise } = useExercisePageStore();
  const { getNextQuestion } = useGetNextExercise();

  useEffect(() => {
    if (rightAnswer) {
      setTimeout(() => {
        setRightAnswer(false);
      }, 7000);
    }
  }, [rightAnswer]);

  //Set the current index and get the next exercise and set the exercise list with the current exercise information
  const handleSuccess = (data: IExercise[]) => {
    const exercises = setCurrentExercise(data);
    const { currentIndex, nextIndex } = getCurrentExerciseIndex(exercises);

    //If there is a next exercise, get the next question
    if (nextIndex) {
      getNextQuestion(exercises[nextIndex].slug);
    }

    setExercisesList(exercises);

    if (!startDate) setStartDate(new Date());

    return { currentIndex, nextIndex, startTime: new Date() }
  };

  //Get the current and next exercise index
  const getCurrentExerciseIndex = (exercises: IExercise[]) => {
    const currentExerciseIndex = exercises.findIndex((exercise) => exercise.slug === slug);

    return { currentIndex: currentExerciseIndex, nextIndex: currentExerciseIndex === exercises.length - 1 ? null : currentExerciseIndex + 1 };
  };

  //Sets the flag selected to the current exercise in the exercises list
  const setCurrentExercise = (exercises: IExercise[]) => {
    const { currentIndex } = getCurrentExerciseIndex(exercises);
    const stateExercises = exercises.length > 0 ? exercises : exercisesList;

    const currentExercise = exercises[currentIndex];

    // Slug atual não está na lista (ex.: filtro aplicado excluiu o exercício
    // que estava aberto). Retorna a lista sem marcar seleção — o caller decide
    // o que fazer (navegar para o primeiro, mostrar empty state, etc.).
    if (!currentExercise) {
      return stateExercises;
    }

    setPreviousExercise(currentExercise);

    const updatedExercises = stateExercises.map((stateExercise) => {
      stateExercise.selected = stateExercise.id === currentExercise.id;

      if (stateExercise.id === currentExercise.id) {
        return { ...stateExercise, ...currentExercise, done: false };
      }

      return stateExercise;
    });

    return updatedExercises;
  };

  const changeExercise = (index: number, _isFavorite?: string | null) => {
    const newSlug = exercisesList[index].slug;
    setRightAnswer(false);
    const search = window.location.search;
    navigate(`/disciplinas/${discipline}/modulo/${module}/assunto/${subject}/exercicio/${newSlug}${search}`);
  };

  const getSelectedAlternatives = (exercise: IExercise) => {
    const selectedAlternativesIds = exercise.alternatives
      .filter((alternative) => alternative.selected)
      .map((alternative) => alternative.id);

    return selectedAlternativesIds;
  };

  function dispatchSetContentAsDoneEvent(id: string, isCorrect: boolean) {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: id,
        isCorrect,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  const updateExercise = async (
    exercise: IExercise,
    action: ExerciseAction = null,
    timeStart?: Date | null
  ) => {
    const updatedExercises = exercisesList.map((stateExercise) =>
      stateExercise.id === exercise.id
        ? { ...stateExercise, ...exercise }
        : stateExercise
    );

    setExercisesList(updatedExercises);

    if (action === 'verifyAnswer') {
      const selectedAlternativesIds = getSelectedAlternatives(exercise);
      const endDate = new Date();

      const time = Math.abs(timeStart!.getTime() - endDate.getTime());

      if (exercise.correct) {
        setRightAnswer(true);
      }

      await saveExerciseAnswer(
        exercise.slug,
        selectedAlternativesIds,
        exercise.correct,
        time,
        'navegacao-livre'
      );

      dispatchSetContentAsDoneEvent(exercise.id, exercise.correct);
    }
  };

  return {
    isContentModal,
    exercises: exercisesList, 
    rightAnswer,
    handleSuccess,
    updateExercise,
    changeExercise,
    setIsContentModal,
  };
};
